import {
  mapProductsFromVariantsAcrossMultipleProducts,
  getVariantStockStatus,
  getTotalInStock,
  getTotalValueFromAddedVariants,
  getTotalValueFromAdddedVariantsInGB,
  mapItemsFromVariants,
} from "./marketingTrackingUtils";
import { itemsDataTemplate } from "./itemsDataTemplate";
import { getTotalUniqueSizes } from "../getTotalUniqueSizes";

export function addToWishlistTemplate({
  product,
  variantsAdded,
  component_name = "pdp_hero",
}) {
  const productAdded = (variantsAdded || []).map((variant) =>
    itemsDataTemplate({
      products: product,
      variant,
      getQuantity: true,
    }),
  );

  return {
    add_to_wishlist: {
      component_name,
      ecommerce: {
        currency: "GBP",
        localCurrencyCode:
          (variantsAdded && variantsAdded[0]?.price?.currency) || "GBP",
        value: product?.price?.xrp?.value || 0,
        valueLocalCurrency: product?.price?.current?.value || 0,
        items: productAdded,
      },
    },
  };
}

export function pageLoadMixAndMatchTemplate(
  parentProduct,
  { hostAndPath, urlParams, requestId },
) {
  const mixAndMatchProducts = parentProduct.products;
  const category = mixAndMatchProducts[0].productType?.name;

  const result = {
    productId: mixAndMatchProducts[0].id,
    productCode: mixAndMatchProducts[0].productCode,
    productUrl: hostAndPath,
    cid: urlParams.cid || null,
    ...(requestId && { request_id: requestId }),
    ...(category && { category }),
    ecommerce: {
      currency: "GBP",
      localCurrencyCode: mixAndMatchProducts[0].price.currency,
      value: mixAndMatchProducts[0].price.xrp.value,
      valueLocalCurrency: mixAndMatchProducts[0].price.current.value,
      items: mapProductsFromVariantsAcrossMultipleProducts({
        mixAndMatchProducts,
        getQuantity: false,
      }),
    },
  };

  return result;
}

export function pageLoadSingleProductTemplate(
  products,
  { hostAndPath, urlParams, requestId },
) {
  const category = products.productType?.name;
  const result = {
    productId: products.id,
    productCode: products.productCode,
    availableSku: getTotalInStock(products.variants),
    availableSizes: getTotalUniqueSizes({ variants: products.variants }),
    productUrl: hostAndPath,
    cid: urlParams.cid || null,
    ...(requestId && { request_id: requestId }),
    variantStockStatus: getVariantStockStatus(products.variants),
    ...(category && { category }),
    ecommerce: {
      currency: "GBP",
      localCurrencyCode: products.variants[0].price.currency,
      value: products.price.xrp.value,
      valueLocalCurrency: products.price.current.value,
      items: mapItemsFromVariants({
        products,
        variants: products.variants,
      }),
    },
  };
  return result;
}

export function addToCartSingleProductTemplate({
  products,
  variantAdded,
  context = "pdp_hero",
  requestId,
}) {
  const productAdded = itemsDataTemplate({
    products,
    variant: variantAdded,
    getQuantity: true,
  });

  return {
    add_to_cart: {
      component_name: context,
      ...(requestId && context === "pdp_hero" && { request_id: requestId }),
      ecommerce: {
        currency: "GBP",
        localCurrencyCode: variantAdded.price.currency,
        value: productAdded.price,
        valueLocalCurrency: productAdded.priceLocalCurrency,
        items: [productAdded],
      },
    },
  };
}

export function addToCartMultipleProductTemplate({
  products,
  variantsAddedList,
  context = "pdp_hero",
  requestId,
}) {
  const productsAdded = mapItemsFromVariants({
    products,
    variants: variantsAddedList,
    getQuantity: true,
  });

  return {
    add_to_cart: {
      component_name: context,
      ...(requestId && context === "pdp_hero" && { request_id: requestId }),
      ecommerce: {
        currency: "GBP",
        localCurrencyCode: variantsAddedList[0].priceObj.currency,
        value: getTotalValueFromAdddedVariantsInGB(productsAdded),
        valueLocalCurrency: getTotalValueFromAddedVariants(productsAdded),
        items: productsAdded,
      },
    },
  };
}

export function notifyMeButtonClickTemplate({
  product,
  variant,
  component_name = "pdp_hero",
}) {
  const item = itemsDataTemplate({ products: product, variant });
  return {
    notify_me_button_click: {
      component_name,
      ecommerce: {
        items: [item],
      },
    },
  };
}

function buildProductLevelItem(product) {
  // Variant-less products (recommendations, and out-of-stock BTL looks) have no
  // SKU, so fall back to a stable product-level identifier for item_id. Read
  // either the looks shape (id/name/price) or the recs shape
  // (productId/productTitle/priceData).
  const productId = product.id ?? product.productId;
  const itemId = product.productCode ?? productId;
  const priceLocalCurrency =
    product.price?.current?.value ?? product.priceData?.current?.value ?? 0;
  const price =
    product.price?.xrp?.value ??
    product.priceData?.xrp?.value ??
    priceLocalCurrency;

  const item = {
    item_id: itemId?.toString(),
    productId: productId?.toString(),
    item_name: product.name ?? product.productTitle,
    item_brand: product.brandName,
    itemType: "Product",
    price,
    priceLocalCurrency,
    google_business_vertical: "retail",
  };

  if (product.productCode) {
    item.productCode = product.productCode.toString();
  }

  return item;
}

export function viewItemListTemplate({ products, listName }) {
  const items = (products || []).map((product) =>
    product.variants && product.variants.length > 0
      ? itemsDataTemplate({
          products: product,
          variant: product.variants[0],
        })
      : buildProductLevelItem(product),
  );

  return {
    view_item_list: {
      item_list_name: listName,
      ecommerce: {
        currency: "GBP",
        items: items,
      },
    },
  };
}
